import { DateTime, Duration } from 'luxon';
import { z } from 'zod';

import { parseISO8601Duration } from '@/utils/dates';
import { zodTimeStamp } from '@/utils/zodUtils';

// Custom duration parser instead of zodDuration — zodDuration includes AnchorDuration
// support (all_day, until_5am_next_day) which doesn't apply to camera intervals.
const cameraDuration = z
  .string()
  .transform((val) => parseISO8601Duration(val))
  .pipe(
    z.custom<Duration<true>>((val) => val instanceof Duration && val.isValid)
  );

export const CAMERA_TYPES = ['axis', 'milesight', 'flock', 'uniview'] as const;
export const CameraTypeEnum = z.enum(CAMERA_TYPES);
export type CameraTypeEnum = z.infer<typeof CameraTypeEnum>;

export const CAMERA_TYPE_LABELS: Record<CameraTypeEnum, string> = {
  axis: 'Axis',
  milesight: 'Milesight',
  flock: 'Flock',
  uniview: 'Uniview',
};

export const DIRECTIONS = ['in', 'out', 'any'] as const;
export const CameraDirectionEnum = z.enum(DIRECTIONS);
export type CameraDirectionEnum = z.infer<typeof CameraDirectionEnum>;

export const DIRECTION_LABELS: Record<CameraDirectionEnum, string> = {
  in: 'Entry',
  out: 'Exit',
  // eslint-disable-next-line id-denylist
  any: 'Any',
};

export const CameraStatusEnum = z.enum(['online', 'offline']);
export type CameraStatusEnum = z.infer<typeof CameraStatusEnum>;

export const CAMERA_HEALTH_STATUSES = [
  'online',
  'offline',
  'degraded',
] as const;
export const CameraHealthStatusEnum = z.enum(CAMERA_HEALTH_STATUSES);
export type CameraHealthStatusEnum = z.infer<typeof CameraHealthStatusEnum>;

export const ALERT_SEVERITIES = ['critical', 'warning', 'info'] as const;
export const AlertSeverityEnum = z.enum(ALERT_SEVERITIES);
export type AlertSeverityEnum = z.infer<typeof AlertSeverityEnum>;

export const SEVERITY_COLORS: Record<
  AlertSeverityEnum,
  'error' | 'warning' | 'info'
> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

export const MaintenanceWindowStatusEnum = z.enum([
  'active',
  'scheduled',
  'expired',
]);
export type MaintenanceWindowStatusEnum = z.infer<
  typeof MaintenanceWindowStatusEnum
>;

export const CameraSchema = z.object({
  serialNumber: z.string(),
  macAddress: z.string(),
  prodShortName: z.string(),
  type: CameraTypeEnum,
  locationId: z.string(),
  defaultDirection: CameraDirectionEnum,
  defaultPlateState: z.string().nullable().optional(),
  status: CameraStatusEnum,
  confidenceScoreThreshold: z.number().nullable(),
  eventIntervalThreshold: cameraDuration.nullable(),
  gracePeriodMinutes: z.number().nullable(),
  plateDetection: z.boolean(),
  plateRecognition: z.boolean(),
  // Defaults to false for responses from an API release that predates the toggle.
  plateStateRecognition: z.boolean().default(false),
  detectVehicle: z.boolean(),
  vehicleRecognition: z.boolean(),
  imageStampEnabled: z.boolean(),
  useEventMoveDirection: z.boolean(),
  lastEventAt: zodTimeStamp.nullable(),
  createdAt: zodTimeStamp,
  updatedAt: zodTimeStamp,
  model: z.string().nullable().optional(),
  firmwareVersion: z.string().nullable().optional(),
  axisOsVersion: z.string().nullable().optional(),
  ipAddress: z.string().nullable().optional(),
  healthStatus: CameraHealthStatusEnum.nullable().optional(),
  lastHealthReportAt: zodTimeStamp.nullable().optional(),
  storageTotalBytes: z.number().nullable().optional(),
  storageUsedBytes: z.number().nullable().optional(),
  temperatureCelsius: z.number().nullable().optional(),
  uptimeSeconds: z.number().nullable().optional(),
  nvrId: z.string().nullable().optional(),
});

export type CameraType = z.infer<typeof CameraSchema>;

export const CameraListSchema = z.object({
  cameras: z.array(CameraSchema),
});

export type CameraListType = z.infer<typeof CameraListSchema>;

export const CameraMetricsSchema = z.object({
  totalCount: z.number(),
  countByStatus: z.array(
    z.object({
      status: CameraStatusEnum,
      count: z.number(),
    })
  ),
  countByType: z.array(
    z.object({
      type: CameraTypeEnum,
      count: z.number(),
    })
  ),
  eventsPerHour: z.array(
    z.object({
      hour: z.number(),
      entry: z.number(),
      exit: z.number(),
      confirmed: z.number(),
    })
  ),
});

export type CameraMetricsType = z.infer<typeof CameraMetricsSchema>;

export const CreateCameraRequestSchema = z.object({
  serialNumber: z.string().min(1),
  macAddress: z.string().min(1),
  prodShortName: z.string().min(1).max(100),
  type: CameraTypeEnum,
  locationId: z.string().min(1),
  defaultDirection: CameraDirectionEnum,
});

export type CreateCameraRequestType = z.infer<typeof CreateCameraRequestSchema>;

export const UpdateCameraRequestSchema = z.object({
  prodShortName: z.string().min(1).max(100),
  locationId: z.string().min(1),
  defaultDirection: CameraDirectionEnum,
  defaultPlateState: z.string().nullable(),
  confidenceScoreThreshold: z.number().min(0).max(1).nullable(),
  eventIntervalThreshold: z
    .string()
    .refine(
      (val) => parseISO8601Duration(val).isValid,
      'Invalid ISO 8601 duration'
    )
    .nullable(),
  gracePeriodMinutes: z.number().min(0).max(1440).nullable(),
  plateDetection: z.boolean(),
  plateRecognition: z.boolean(),
  plateStateRecognition: z.boolean(),
  detectVehicle: z.boolean(),
  vehicleRecognition: z.boolean(),
  imageStampEnabled: z.boolean(),
  useEventMoveDirection: z.boolean(),
});

export type UpdateCameraRequestType = z.infer<typeof UpdateCameraRequestSchema>;

export const MaintenanceWindowSchema = z.object({
  id: z.string(),
  cameraSerialNumber: z.string(),
  reason: z.string(),
  status: MaintenanceWindowStatusEnum,
  startAt: zodTimeStamp,
  endAt: zodTimeStamp,
  createdAt: zodTimeStamp,
});

export type MaintenanceWindowType = z.infer<typeof MaintenanceWindowSchema>;

export const MaintenanceWindowListSchema = z.object({
  maintenanceWindows: z.array(MaintenanceWindowSchema),
});

export type MaintenanceWindowListType = z.infer<
  typeof MaintenanceWindowListSchema
>;

export const CreateMaintenanceWindowRequestSchema = z.object({
  reason: z.string().min(1).max(100),
  startAt: z.string(),
  endAt: z.string(),
});

export type CreateMaintenanceWindowRequestType = z.infer<
  typeof CreateMaintenanceWindowRequestSchema
>;

export const UpdateMaintenanceWindowRequestSchema =
  CreateMaintenanceWindowRequestSchema;

export type UpdateMaintenanceWindowRequestType =
  CreateMaintenanceWindowRequestType;

// Uses z.custom instead of zodTimeStamp because form values are already DateTime
// objects from DateTimePicker, not API strings that need parsing.
export const MaintenanceWindowFormSchema = z
  .object({
    reason: z.string().min(1, 'Reason is required'),
    startAt: z.custom<DateTime>(
      (val) => DateTime.isDateTime(val) && val.isValid,
      'Start date is required'
    ),
    endAt: z.custom<DateTime>(
      (val) => DateTime.isDateTime(val) && val.isValid,
      'End date is required'
    ),
  })
  .refine((data) => data.endAt > data.startAt, {
    message: 'End date must be after start date',
    path: ['endAt'],
  });

// Camera Alerts

export const CameraAlertSchema = z.object({
  id: z.string(),
  cameraId: z.string().nullable().optional(),
  nvrId: z.string().nullable().optional(),
  locationId: z.string(),
  alertType: z.string(),
  severity: AlertSeverityEnum,
  message: z.string(),
  acknowledged: z.boolean(),
  acknowledgedBy: z.string().nullable().optional(),
  acknowledgedAt: zodTimeStamp.nullable().optional(),
  resolvedAt: zodTimeStamp.nullable().optional(),
  createdAt: zodTimeStamp,
  updatedAt: zodTimeStamp.optional(),
});

export type CameraAlertType = z.infer<typeof CameraAlertSchema>;

export const CameraAlertListSchema = z.object({
  alerts: z.array(CameraAlertSchema),
});

export type CameraAlertListType = z.infer<typeof CameraAlertListSchema>;

// Fleet Health

export const FleetHealthSchema = z.object({
  totalCameras: z.number(),
  onlineCameras: z.number(),
  degradedCameras: z.number(),
  offlineCameras: z.number(),
  totalNvrs: z.number(),
  onlineNvrs: z.number(),
  degradedNvrs: z.number(),
  offlineNvrs: z.number(),
  alertCounts: z.array(
    z.object({
      severity: AlertSeverityEnum,
      count: z.number(),
    })
  ),
});

export type FleetHealthType = z.infer<typeof FleetHealthSchema>;
